// store/useClassificationStore.js
import { create } from 'zustand';

const useClassificationStore = create((set, get) => ({
  // ===== CLASSES STATE =====
  classes: [],
  classCount: "",

  // Initialize classes based on count (fresh start when no classes exist)
  initializeClasses: () => {
    const { classCount } = get();
    const count = parseInt(classCount) || 0;

    if (count <= 0) {
      console.warn('Class count must be a positive number');
      return;
    }

    const newClasses = Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      name: `Class ${i + 1}`,
      images: []
    }));

    set({ classes: newClasses });
  },

  // Add new classes without resetting existing ones (preserves all data)
  /**
   * Add classes.
   *
   * `count` is an explicit DELTA — how many new classes to append. With no
   * argument it keeps the original meaning: top up to the `classCount` typed in
   * the right-hand panel, which is what the "Create Classes" button has always
   * done.
   *
   * The parameter is the fix for a silent failure. `useAutoGroup` calls
   * `addClasses(plan.classesNeeded)` after the server reports how many groups
   * it actually found — and this action took NO argument, so the number was
   * dropped and it read `classCount` instead. `classCount` is `""` by default
   * and is never set when a saved version is loaded, so `parseInt("") || 0` was
   * 0, the guard returned early, and NO classes were created. Every assignment
   * past the existing class count then hit the `classId == null` skip in
   * `useAutoGroup`, so **not one image moved** and nothing said why.
   */
  addClasses: (count) => {
    const { classCount, classes } = get();
    const list = Array.isArray(classes) ? classes : [];

    const delta = Number.isInteger(count) && count > 0
      ? count
      : Math.max(0, (parseInt(classCount, 10) || 0) - list.length);

    if (delta <= 0) return;

    // A class loaded from a saved version can carry a non-numeric id, and
    // `Math.max` over one yields NaN — which would then never match the
    // `cls.id === classId` compare in addClassImage, silently dropping every
    // image assigned to it. Only numeric ids count towards the next id.
    const numericIds = list.map((c) => Number(c.id)).filter((n) => Number.isFinite(n));
    const maxId = numericIds.length ? Math.max(...numericIds) : 0;

    const newClasses = Array.from({ length: delta }, (_, i) => ({
      id: maxId + i + 1,
      name: `Class ${maxId + i + 1}`,
      images: []
    }));

    set({ classes: [...list, ...newClasses], classCount: list.length + delta });
  },

  // Reduce classes (removes from the end; images in removed classes are lost)
  reduceClassesTo: (newCount) => {
    const { classes } = get();
    const count = parseInt(newCount) || 0;

    if (!Array.isArray(classes) || count <= 0 || count >= classes.length) {
      return;
    }

    const trimmed = classes.slice(0, count);
    set({ classes: trimmed, classCount: count });
  },

  // Update class name
  updateClassName: (classId, newName) => {
    const { classes } = get();

    if (!Array.isArray(classes)) {
      console.error('Classes state is corrupted, resetting to empty array');
      set({ classes: [] });
      return;
    }

    set({
      classes: classes.map(cls =>
        cls.id === classId ? { ...cls, name: newName } : cls
      )
    });
  },

  // Add image to a specific class
  addClassImage: (classId, image) => {
    const { classes } = get();

    set({
      classes: classes.map(cls =>
        cls.id === classId
          ? { ...cls, images: [...cls.images, image] }
          : cls
      )
    });
  },

  // Remove image from a specific class
  removeClassImage: (classId, imageId) => {
    const { classes, selectedImage, selectedImages } = get();

    // Check if we need to update selected image
    const updateSelectedImage = selectedImage && selectedImage.id === imageId;
    const newSelectedImages = new Set(selectedImages);
    newSelectedImages.delete(imageId);

    set({
      classes: classes.map(cls =>
        cls.id === classId
          ? { ...cls, images: cls.images.filter(img => img.id !== imageId) }
          : cls
      ),
      selectedImage: updateSelectedImage ? null : selectedImage,
      selectedImages: newSelectedImages
    });
  },

  // Remove image from all classes
  removeImageFromClasses: (imageId) => {
    const { classes, selectedImage, selectedImages } = get();

    // Check if we need to update selected image
    const updateSelectedImage = selectedImage && selectedImage.id === imageId;
    const newSelectedImages = new Set(selectedImages);
    newSelectedImages.delete(imageId);

    set({
      classes: classes.map(cls => ({
        ...cls,
        images: cls.images.filter(img => img.id !== imageId)
      })),
      selectedImage: updateSelectedImage ? null : selectedImage,
      selectedImages: newSelectedImages
    });
  },

  // Remove multiple images from all classes
  removeImagesFromClasses: (imageIds) => {
    const { classes, selectedImage, selectedImages } = get();
    const idsSet = new Set(imageIds);

    // Check if we need to update selected image
    const updateSelectedImage = selectedImage && idsSet.has(selectedImage.id);
    const newSelectedImages = new Set(selectedImages);
    imageIds.forEach(id => newSelectedImages.delete(id));

    set({
      classes: classes.map(cls => ({
        ...cls,
        images: cls.images.filter(img => !idsSet.has(img.id))
      })),
      selectedImage: updateSelectedImage ? null : selectedImage,
      selectedImages: newSelectedImages
    });
  },

  // Add multiple images to a class
  updateClassImages: (classId, newImages) => {

    const { classes } = get();

    set({
      classes: classes.map(cls =>
        cls.id === classId
          ? { ...cls, images: [...cls.images, ...newImages] }
          : cls
      )
    });
  },

  // Set class count
  setClassCount: (count) => set({ classCount: count }),

  setClasses: (value) => {
    set({ classes: value })
  },

  // ===== UNSTRUCTURED IMAGES STATE =====
  unstructuredImages: [],
  setunstructuredImages: (value) => { set({ unstructuredImages: value }) },

  // Add images to unstructured, avoiding duplicates by name
  // addUnstructuredImages: (newImages) => {
  //   const { unstructuredImages } = get();
  //   const existingNames = new Set(unstructuredImages.map(img => img.name));
  //   const uniqueNewImages = newImages.filter(img => !existingNames.has(img.name));

  //   set({ 
  //     unstructuredImages: [...unstructuredImages, ...uniqueNewImages] 
  //   });
  // },

  addUnstructuredImages: (newImages) => {
    set((state) => {
      const updated = [...state.unstructuredImages];

      newImages.forEach((newImg) => {
        const index = updated.findIndex((img) => img.name === newImg.name);

        if (index !== -1) {
          // Update existing image (merge url or other fields)
          updated[index] = { ...updated[index], ...newImg };
        } else {
          // Add as new
          updated.push(newImg);
        }
      });

      return { unstructuredImages: updated };
    });
  },

  // Remove single image from unstructured and update selection if needed
  removeUnstructuredImage: (imageId) => {
    const { unstructuredImages, selectedImage, selectedImages } = get();

    // Check if the image being removed is the currently selected image
    const updateSelectedImage = selectedImage && selectedImage.id === imageId;

    // Remove from selected images set
    const newSelectedImages = new Set(selectedImages);
    newSelectedImages.delete(imageId);

    set({
      unstructuredImages: unstructuredImages.filter(img => img.id !== imageId),
      selectedImage: updateSelectedImage ? null : selectedImage,
      selectedImages: newSelectedImages
    });
  },

  // Remove multiple images from unstructured and update selection if needed
  removeUnstructuredImages: (imageIds) => {
    const { unstructuredImages, selectedImage, selectedImages } = get();
    const idsSet = new Set(imageIds);

    // Check if the selected image is among those being removed
    const updateSelectedImage = selectedImage && idsSet.has(selectedImage.id);

    // Remove from selected images set
    const newSelectedImages = new Set(selectedImages);
    imageIds.forEach(id => newSelectedImages.delete(id));

    set({
      unstructuredImages: unstructuredImages.filter(img => !idsSet.has(img.id)),
      selectedImage: updateSelectedImage ? null : selectedImage,
      selectedImages: newSelectedImages
    });
  },

  // Clear all unstructured images
  clearUnstructuredImages: () => {
    const { selectedImage, selectedImages } = get();

    // Check if selected image is in unstructured images
    const selectedInUnstructured = selectedImage &&
      !selectedImage.assignedClass; // Assuming unstructured images have no assignedClass

    // Filter selected images to remove those that are unstructured
    const newSelectedImages = new Set(selectedImages);
    if (selectedInUnstructured) {
      newSelectedImages.delete(selectedImage.id);
    }

    set({
      unstructuredImages: [],
      selectedImage: selectedInUnstructured ? null : selectedImage,
      selectedImages: newSelectedImages
    });
  },

  // ===== SELECTION STATE =====
  selectedImage: null,
  selectedImages: new Set(),

  // Anchor + scope for explorer-style multi-select (see useClassificationLabelHook
  // .applySelection). These live in the STORE, not in the hook, because the hook is
  // instantiated separately by ClassificationLeft, ClassificationRight and
  // ClassificationMiddleTop -- a useState anchor is a different value in each of
  // them, so a Shift-range started in one panel could never be read by another.
  //
  // selectionAnchor: id the next Shift+click measures its range from.
  // selectionScope:  'unstructured' or a class id -- the ONE list the current
  //                  multi-selection belongs to. Selecting in another list starts
  //                  fresh there, the way a file manager cannot select across two
  //                  folders, which also keeps bulk assign unambiguous.
  selectionAnchor: null,
  selectionScope: null,
  setSelectionAnchor: (imageId) => set({ selectionAnchor: imageId }),
  setSelectionScope: (scope) => set({ selectionScope: scope }),

  // Set selected image
  setSelectedImage: (image) => set({ selectedImage: image }),

  // Set selected images
  setSelectedImages: (images) => {
    if (images instanceof Set) {
      set({ selectedImages: images });
    } else if (Array.isArray(images)) {
      set({ selectedImages: new Set(images) });
    } else {
      console.error('Selected images must be a Set or Array');
    }
  },

  // Add to selection
  addToSelection: (imageId) => {
    const { selectedImages } = get();
    const newSelection = new Set(selectedImages);
    newSelection.add(imageId);
    set({ selectedImages: newSelection });
  },

  // Remove from selection
  removeFromSelection: (imageId) => {
    const { selectedImages, selectedImage } = get();
    const newSelection = new Set(selectedImages);
    newSelection.delete(imageId);

    // Also check if we need to update selectedImage
    const updateSelectedImage = selectedImage && selectedImage.id === imageId;

    set({
      selectedImages: newSelection,
      selectedImage: updateSelectedImage ? null : selectedImage
    });
  },

  // Clear selection
  clearSelection: () => set({
    selectedImages: new Set(),
    selectedImage: null,
    selectionAnchor: null,
    selectionScope: null
  }),

  // ===== DRAG & DROP STATE =====
  dragOver: false,
  activeDragClass: null,

  // Set drag over state
  setDragOver: (value) => set({ dragOver: value }),

  // Set active drag class
  setActiveDragClass: (classId) => set({ activeDragClass: classId }),

  // ===== UI STATE =====
  showDeleteButton: null,

  // Set which image should show delete button
  setShowDeleteButton: (imageId) => set({ showDeleteButton: imageId }),


  // ===== File Uploader progress =====

  isUploading: false,
  uploadProgress: 0,
  totalUploads: 0,
  completedUploads: 0,

  // Add these actions
  setUploadStatus: (isUploading, totalUploads = 0, completedUploads = 0) =>
    set({ isUploading, totalUploads, completedUploads, uploadProgress: totalUploads > 0 ? (completedUploads / totalUploads) * 100 : 0 }),
  incrementUploadProgress: () =>
    set(state => {
      const completed = state.completedUploads + 1;
      const progress = state.totalUploads > 0 ? (completed / state.totalUploads) * 100 : 0;
      return { completedUploads: completed, uploadProgress: progress };
    }),
  resetUploadStatus: () =>
    set({ isUploading: false, uploadProgress: 0, totalUploads: 0, completedUploads: 0 }),

  // FOR VERSION POPUP
  newVersion: null,
  setNewVersion: (value) => {
    set({ newVersion: value })
  },
  isModelOpen: false,
  setIsModelOpen: (value) => {
    set({ isModelOpen: value })
  },
  // Inside useClassificationStore definition
  resetAll: () => {
    set({
      classes: [],
      classCount: 0,
      unstructuredImages: [],
      selectedImage: null,
      selectedImages: new Set(),
      selectionAnchor: null,
      selectionScope: null,
      dragOver: false,
      activeDragClass: null,
      showDeleteButton: false,
      // Reset upload state
      isUploading: false,
      uploadProgress: 0,
      completedUploads: 0,
      totalUploads: 0,
    });
  },
}));

export default useClassificationStore;